
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { WifiOff, Download, RotateCcw, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { invalidateServiceWorkerCache } from '@/utils/serviceWorkerUtils';
import { 
  shouldCheckForUpdate, 
  markUpdateCheckTime,
  detectMobileBrowser 
} from '@/utils/versionTracker';

const PWAStatus: React.FC = () => {
  // Initialize with safe defaults - NO navigator access until after mount
  const [isOnline, setIsOnline] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCheckingForUpdate, setIsCheckingForUpdate] = useState(false);
  const [showManualUpdate, setShowManualUpdate] = useState(false);
  const [isSafeToRun, setIsSafeToRun] = useState(false);
  const [platformInfo, setPlatformInfo] = useState({ isIOS: false, isAndroid: false, isMobile: false });
  const [autoUpdateCountdown, setAutoUpdateCountdown] = useState<number | null>(null);
  const { toast } = useToast();
  const updateCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);
  const androidCacheCleared = useRef(false);
  
  // Safely detect platform AFTER mount to prevent iOS Safari crashes
  useEffect(() => {
    const initPlatform = () => {
      try {
        if (typeof navigator === 'undefined') {
          setIsSafeToRun(true);
          return;
        }
        
        const info = detectMobileBrowser();
        setPlatformInfo(info);
        setIsOnline(navigator.onLine);
        
        // Delay PWA checks on iOS to prevent crashes during cold start
        const delay = info.isIOS ? 2000 : 100;
        setTimeout(() => {
          setIsSafeToRun(true);
        }, delay);
      } catch (error) {
        console.warn('Platform detection failed, using defaults');
        setIsSafeToRun(true);
      }
    };
    
    const timeoutId = setTimeout(initPlatform, 50);
    return () => clearTimeout(timeoutId);
  }, []);
  
  const { isIOS, isAndroid, isMobile } = platformInfo;

  // Auto-update with countdown
  const startAutoUpdateCountdown = useCallback(() => {
    // Start 5 second countdown
    setAutoUpdateCountdown(5);
    
    countdownInterval.current = setInterval(() => {
      setAutoUpdateCountdown(prev => {
        if (prev === null || prev <= 1) {
          // Time's up - trigger the update
          clearInterval(countdownInterval.current!);
          handleUpdate();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      toast({
        title: "Update not available",
        description: "Service workers are not supported in this browser",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUpdating(true);
      clearInterval(countdownInterval.current!);
      setAutoUpdateCountdown(null);
      
      // Clear all caches aggressively
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        
      }
      
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // Short delay then reload
        setTimeout(() => {
          window.location.reload();
        }, isIOS ? 1000 : 500);
      } else {
        // Force registration update and reload
        if (registration) {
          await registration.update();
        }
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
      
      toast({
        title: "Updating app",
        description: "Loading the latest version...",
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Error updating app:', error);
      setIsUpdating(false);
      // Force reload as fallback
      window.location.reload();
    }
  }, [toast, isIOS]);

  const checkForUpdates = useCallback(async (manual = false) => {
    if (!('serviceWorker' in navigator) || (!manual && !shouldCheckForUpdate())) {
      return;
    }

    if (isCheckingForUpdate && !manual) {
      return;
    }

    setIsCheckingForUpdate(true);
    if (!manual) {
      markUpdateCheckTime();
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (registration) {
        await registration.update();
        
        if (registration.waiting) {
          setUpdateAvailable(true);
          // Auto-start countdown for automatic update
          startAutoUpdateCountdown();
          return;
        }

        if (manual) {
          toast({
            title: "App is up to date",
            description: "You're running the latest version.",
            duration: 3000,
          });
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
      if (manual) {
        toast({
          title: "Update check failed",
          description: "Unable to check for updates. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsCheckingForUpdate(false);
    }
  }, [toast, startAutoUpdateCountdown]);

  useEffect(() => {
    if (!isSafeToRun) return;
    
    const handleOnline = async () => {
      setIsOnline(true);
      await invalidateServiceWorkerCache();
      checkForUpdates();
      
      toast({
        title: "Back online",
        description: "Connection restored and checking for updates",
        duration: 3000,
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (updateCheckInterval.current) {
        clearInterval(updateCheckInterval.current);
        updateCheckInterval.current = null;
      }
      
      toast({
        title: "You're offline",
        description: "Using cached data. Some features may be limited.",
        duration: 5000,
      });
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        checkForUpdates();
      }
    };

    const handleFocus = () => {
      if (navigator.onLine) {
        checkForUpdates();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    if ('serviceWorker' in navigator) {
      const handleControllerChange = () => {
        window.location.reload();
      };

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      // Listen for SW activation messages that request reload
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_ACTIVATED' && event.data?.forceReload) {
          window.location.reload();
        }
        if (event.data?.type === 'ANDROID_CACHE_CLEARED') {
          androidCacheCleared.current = true;
          setTimeout(() => checkForUpdates(true), 1000);
        }
      });

      // Initial check for waiting service worker
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          setUpdateAvailable(true);
          startAutoUpdateCountdown();
        }
        
        if (registration) {
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                  startAutoUpdateCountdown();
                }
              });
            }
          });
        }
      });

      // Initial update check
      checkForUpdates();

      // Set up periodic update checks - more frequent now
      if (navigator.onLine) {
        const checkInterval = isAndroid ? 3 * 60 * 1000 : 5 * 60 * 1000; // 3 min Android, 5 min others
        updateCheckInterval.current = setInterval(() => {
          if (navigator.onLine && !isCheckingForUpdate) {
            checkForUpdates();
          }
        }, checkInterval);
      }

      // Show manual update option on mobile after a delay
      if (isMobile) {
        setTimeout(() => setShowManualUpdate(true), 15000); // Show after 15 seconds
      }
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      
      if (updateCheckInterval.current) {
        clearInterval(updateCheckInterval.current);
      }
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [toast, checkForUpdates, isMobile, isSafeToRun, isAndroid, startAutoUpdateCountdown, isCheckingForUpdate]);

  // Early return if not safe to run
  if (!isSafeToRun) {
    return null;
  }

  const handleManualUpdateCheck = () => {
    checkForUpdates(true);
  };

  const cancelAutoUpdate = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }
    setAutoUpdateCountdown(null);
  };

  if (!isOnline) {
    return (
      <div className="fixed top-16 left-4 right-4 z-40 md:left-auto md:right-4 md:w-64">
        <Badge variant="secondary" className="w-full justify-center gap-2 py-2">
          <WifiOff className="h-4 w-4" />
          Offline Mode
        </Badge>
      </div>
    );
  }

  if (updateAvailable) {
    return (
      <div className="fixed top-16 left-4 right-4 z-40 md:left-auto md:right-4 md:w-80">
        <div className="bg-primary text-primary-foreground rounded-lg p-3 shadow-lg">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {isUpdating ? (
                <RotateCcw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span className="font-medium text-sm">
                {isUpdating 
                  ? 'Updating...' 
                  : autoUpdateCountdown !== null 
                    ? `Updating in ${autoUpdateCountdown}s...`
                    : 'Update Available'
                }
              </span>
            </div>
            <div className="flex items-center gap-1">
              {!isUpdating && autoUpdateCountdown !== null && (
                <Button
                  onClick={cancelAutoUpdate}
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-primary-foreground hover:bg-primary-foreground/20"
                >
                  Later
                </Button>
              )}
              <Button
                onClick={handleUpdate}
                disabled={isUpdating}
                variant="secondary"
                size="sm"
                className="h-7 px-3"
              >
                {isUpdating ? 'Please wait...' : 'Update Now'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show manual update check for mobile users
  if (showManualUpdate && isMobile && isOnline) {
    return (
      <div className="fixed bottom-20 right-4 z-40">
        <Button
          onClick={handleManualUpdateCheck}
          disabled={isCheckingForUpdate}
          variant="outline"
          size="sm"
          className="shadow-lg"
        >
          {isCheckingForUpdate ? (
            <RotateCcw className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>
    );
  }

  return null;
};

export default PWAStatus;
